import { useLayoutEffect } from 'react';

import metrics, { IMetric } from 'maps/metrics';

import getMobileSortingOptions from './getMobileSortingOptions';

import useSorting from './useSorting';
import isSortMetricValid from './isSortMetricValid';

export default useSortingWithEffects;

interface ISortingWithEffects {
  sortOrder: string;
  setSortOrder: (...args: any[]) => any;
  sortMetric: IMetric | undefined;
  setSortMetric: (...args: any[]) => any;
  switchSortOrder: Function;
  changeSortMetric: (...args: any[]) => any;
}

function useSortingWithEffects({
  initialSortMetric = metrics['performance_this_week_to_date'],
  apiType,
  browseEntityType,
  tableHeadersType,
}: {
  initialSortMetric: IMetric | undefined;
  apiType: string;
  browseEntityType: string;
  tableHeadersType?: string;
}): ISortingWithEffects {
  const {
    sortOrder,
    setSortOrder,
    sortMetric,
    setSortMetric,
    switchSortOrder,
    changeSortMetric,
  } = useSorting(initialSortMetric);

  /*
   * We save the `browseEntityType` persistenly in component settings,
   * but it's possible we render before that comes in, leaving us with the
   * default value. To prevent a possible mismatch with the sort metric,
   * this effect makes sure the correct sort metric is used.
   */
  useLayoutEffect(() => {
    const isLabel = apiType === 'label';

    if (
      !isSortMetricValid(
        browseEntityType,
        sortMetric?.value as string,
        tableHeadersType
      ) &&
      sortMetric?.value !== 'custom'
    ) {
      if (isLabel) setSortMetric(metrics['grouped_track_count']);
      else setSortMetric(initialSortMetric);
    }
  }, [
    apiType,
    sortMetric,
    browseEntityType,
    setSortMetric,
    initialSortMetric,
    tableHeadersType,
  ]);

  /*
   * When the users has selected 'version' as the browse entity type
   * and has selected 'Version name' as the selected metric, when they change
   * to a different browse entity type it still says 'Version name' but it should
   * be '[browseEntityType] name'. This effect makes sure that keeps in sync.
   */

  useLayoutEffect(() => {
    if (
      sortMetric?.value === 'displayName' &&
      browseEntityType !== 'item name' &&
      tableHeadersType !== 'standard' &&
      tableHeadersType !== 'toplists'
    ) {
      const displayNameOption = getMobileSortingOptions(
        browseEntityType,
        tableHeadersType
      )[0];
      setSortMetric(displayNameOption);
    }
  }, [browseEntityType, sortMetric, setSortMetric, tableHeadersType]);

  return {
    sortOrder,
    setSortOrder,
    sortMetric,
    setSortMetric,
    switchSortOrder,
    changeSortMetric,
  };
}
